import type { FetchResponse } from 'ofetch'
import { tryUseNuxtApp, useCookie, useRequestHeaders, useRuntimeConfig } from '#imports'
import type {
  CacheItem,
  ErrorInterceptor,
  HttpClient,
  HttpResponse,
  PendingRequest,
  RequestConfig,
  RequestError,
  RequestInterceptor,
  ResponseInterceptor
} from '~/types/http'
import { useLoadingStore } from '~/stores/loadingStore'


const REQUEST_TIMEOUT = 30_000
const DEFAULT_CACHE_TIME = 5 * 60 * 1000
const ENV_BASE_URL_KEYS = ['NUXT_PUBLIC_API_BASE', 'PUBLIC_API_BASE', 'VITE_API_BASE', 'API_BASE']

/**
 * SSR 友好的 HTTP 封装，基于 fetch / $fetch。
 * - 自动处理 Token 注入（客户端 localStorage、服务端 Cookie/请求头）
 * - 内建请求/响应/错误拦截器，便于统一处理鉴权、埋点、格式转换
 * - 支持自定义缓存、超时控制、重复请求取消、自动重试
 */
export class HttpRequest implements HttpClient {
  private baseURL = '' // 优先使用构造参数，否则按需读取 runtime config
  private defaultTimeout = REQUEST_TIMEOUT
  private requestInterceptors: RequestInterceptor[] = [] // 请求发出前的拦截器（追加头、签名、埋点等）
  private responseInterceptors: ResponseInterceptor[] = [] // 响应统一格式化，例如 data 包裹、success 标志等
  private errorInterceptors: ErrorInterceptor[] = [] // 错误集中处理（401 跳转登录、展示错误提示等）
  private cache = new Map<string, CacheItem>() // 自定义缓存（与 fetch cache 不同，可跨环境复用）
  private pendingRequests = new Map<string, PendingRequest>() // 记录正在进行的请求，用于去重与批量取消
  private loadingCount = 0 // 持有全局 loading 计数，便于外部监听

  /**
   * 可选地传入初始 baseURL；否则运行时按需读取配置。
   */
  constructor(baseURL?: string) {
    this.baseURL = baseURL || ''
    this.initDefaultInterceptors()
  }

  /**
   * 注册默认的请求/响应/错误拦截器，覆盖基础用例。
   */
  private initDefaultInterceptors() {
    this.addRequestInterceptor(async (config) => {
      const headers: Record<string, string> = { ...(config.headers || {}) }
      const token = await this.getToken()

      if (token && !headers.Authorization) {
        headers.Authorization = `Bearer ${token}`
      }

      const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData

      if (config.data && !headers['Content-Type'] && !isFormData) {
        headers['Content-Type'] = 'application/json'
      }

      if (headers['Content-Type'] && isFormData) {
        delete headers['Content-Type']
      }

      return {
        ...config,
        headers
      }
    })

    this.addResponseInterceptor(response => response)

    this.addErrorInterceptor(error => error)
  }

  /**
   * 同时兼容客户端（localStorage）与服务端（Cookie/请求头）的 Token 解析。
   */
  private async getToken(): Promise<string | null> {
    if (process.client) {
      try {
        const localToken = localStorage.getItem('token')
        if (localToken) return localToken
      } catch {
        // ignore and try cookie fallback
      }

      try {
        const cookie = useCookie<string | null>('token')
        if (cookie?.value) {
          return cookie.value
        }
      } catch {
        // ignore and try document.cookie fallback
      }

      try {
        if (typeof document !== 'undefined') {
          const cookies = document.cookie.split(';')
          for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=')
            if (name === 'token') {
              return value || null
            }
          }
        }
      } catch {
        return null
      }

      return null
    }

    try {
      const cookie = useCookie<string | null>('token')
      if (cookie?.value) {
        return cookie.value
      }

      const headers = useRequestHeaders(['authorization'])
      const authorization = headers.authorization

      if (authorization?.startsWith('Bearer ')) {
        return authorization.slice(7)
      }
    } catch {
      return null
    }

    return null
  }

  /**
   * 注册请求拦截器。
   */
  addRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor)
  }

  /**
   * 注册响应拦截器。
   */
  addResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor)
  }

  /**
   * 注册错误拦截器。
   */
  addErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors.push(interceptor)
  }

  /**
   * 移除指定请求拦截器。
   */
  removeRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors = this.requestInterceptors.filter(item => item !== interceptor)
  }

  /**
   * 移除指定响应拦截器。
   */
  removeResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors = this.responseInterceptors.filter(item => item !== interceptor)
  }

  /**
   * 移除指定错误拦截器。
   */
  removeErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors = this.errorInterceptors.filter(item => item !== interceptor)
  }

  /**
   * 动态解析 baseURL：优先构造参数，其次读取 runtime config。
   */
  private getBaseURL(): string {
    if (this.baseURL) {
      return this.baseURL
    }

    const runtimeBaseURL = this.readRuntimeConfigBaseURL()
    if (runtimeBaseURL) {
      this.baseURL = runtimeBaseURL
      return runtimeBaseURL
    }

    const envBaseURL = this.readEnvBaseURL()
    if (envBaseURL) {
      this.baseURL = envBaseURL
      return envBaseURL
    }

    return ''
  }

  /**
   * 从 Nuxt runtime config 中解析 baseURL，仅在 Nuxt 实例已就绪时执行。
   */
  private readRuntimeConfigBaseURL(): string | null {
    const nuxtApp = tryUseNuxtApp()
    if (!nuxtApp) {
      return null
    }

    const config = useRuntimeConfig()
    const apiBase = (config.public?.apiBase ?? config.apiBase) as string | undefined

    if (typeof apiBase === 'string' && apiBase.length > 0) {
      return apiBase
    }

    return null
  }

  /**
   * 从环境变量（process.env / process.env）中解析 baseURL，兼容不同构建工具。
   */
  private readEnvBaseURL(): string | null {
    for (const key of ENV_BASE_URL_KEYS) {
      const value = this.getEnvVar(key)
      if (typeof value === 'string' && value.length > 0) {
        return value
      }
    }

    return null
  }

  private getEnvVar(key: string): string | undefined {
    const metaEnv = typeof process !== 'undefined' ? (process as any).env : undefined
    if (metaEnv?.[key]) {
      return metaEnv[key] as string
    }

    const nodeProcess = typeof globalThis !== 'undefined' ? (globalThis as any).process : undefined
    const envValue = nodeProcess?.env?.[key]
    if (envValue) {
      return envValue
    }

    return undefined
  }

  /**
   * 拼装完整请求地址，兼容绝对 / 相对路径。
   */
  private buildURL(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }

    const baseURL = this.getBaseURL()

    if (!baseURL) {
      return url.startsWith('/') ? url : `/${url}`
    }

    const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL
    const cleanURL = url.startsWith('/') ? url : `/${url}`

    return `${cleanBaseURL}${cleanURL}`
  }

  /**
   * 构建查询字符串，支持数组、多类型值。
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const search = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) {
        return
      }

      if (Array.isArray(value)) {
        value.forEach(item => search.append(key, String(item)))
        return
      }

      search.append(key, String(value))
    })

    return search.toString()
  }

  /**
   * 顺序执行请求拦截器，支持异步。
   */
  private async executeRequestInterceptors(config: RequestConfig) {
    let result = { ...config }

    for (const interceptor of this.requestInterceptors) {
      result = await interceptor(result)
    }

    return result
  }

  /**
   * 顺序执行响应拦截器，最终返回标准 HttpResponse。
   */
  private async executeResponseInterceptors<T>(response: HttpResponse<T>) {
    let result: HttpResponse<any> = response

    for (const interceptor of this.responseInterceptors) {
      result = await interceptor(result)
    }

    return result as HttpResponse<T>
  }

  /**
   * 顺序执行错误拦截器。
   * 如果拦截器返回 undefined，则认为“不修改错误”，继续传递当前错误对象。
   * 始终返回一个标准化后的 RequestError，便于调用方统一处理。
   */
  private async executeErrorInterceptors(error: RequestError) {
    let result: RequestError = error

    for (const interceptor of this.errorInterceptors) {
      const next = await interceptor(result)
      if (next) {
        result = next
      }
    }

    return result
  }

  /**
   * 统一创建 RequestError，记录上下文便于调试。
   */
  private createRequestError(message: string, code?: number | string, response?: Response | FetchResponse<unknown>, config?: RequestConfig, originalError?: Error): RequestError {
    const error = new Error(message) as RequestError
    error.code = code
    error.response = response
    error.config = config
    error.originalError = originalError
    return error
  }

  /**
   * 解析响应数据：兼容 $fetch.raw / 原生 fetch，按 content-type 自动转换。
   */
  private async handleResponse<T>(response: Response | FetchResponse<T>, config: RequestConfig): Promise<HttpResponse<T>> {
    let payload: unknown

    if ('_data' in response && response._data !== undefined) {
      payload = response._data
    } else {
      const contentType = response.headers?.get?.('content-type')

      if (contentType?.includes('application/json')) {
        payload = await (response as Response).clone().json().catch(() => null)
      } else if (contentType?.includes('text/')) {
        payload = await (response as Response).clone().text().catch(() => null)
      } else {
        payload = await (response as Response).clone().arrayBuffer().catch(() => null)
      }
    }

    const status = response.status ?? 0
    const success = status >= 200 && status < 300

    const payloadObject = (payload as { data?: unknown, success?: boolean, message?: string } | null) || {}

    const httpResponse: HttpResponse<T> = {
      code: status,
      data: (payloadObject.data ?? payload) as T,
      success: payloadObject.success ?? success,
      message: payloadObject.message ?? response.statusText ?? '',
      _raw: response
    }

    if (config.skipResponseInterceptor) {
      return httpResponse
    }

    return this.executeResponseInterceptors(httpResponse)
  }

  /**
   * 统一错误处理：包装 Error -> RequestError，执行错误拦截器。
   * 不再承担 UI 提示等职责，仅负责生成和规范化错误对象。
   */
  private async handleError(error: Error | RequestError, config: RequestConfig) {
    let normalized: RequestError

    if ('code' in error || 'config' in error) {
      normalized = error as RequestError
    } else {
      normalized = this.createRequestError(error.message || 'Request failed', undefined, undefined, config, error)
    }

    // 优先使用服务端返回的 message，避免 ofetch/FETCH 包装的噪声信息
    const responseData
      = (normalized as { data?: unknown })?.data
        ?? (normalized as { response?: { _data?: unknown, data?: unknown } })?.response?._data
        ?? (normalized as { response?: { data?: unknown } })?.response?.data
    const responseStatus: number | undefined
      = (normalized as { response?: { _data?: { code?: number }, status?: number }, status?: number })?.response?._data?.code
        ?? (normalized as { response?: { status?: number }, status?: number })?.response?.status
        ?? (normalized as { status?: number })?.status

    if (responseStatus === 400 && (responseData as any)?.message) {
      normalized.message = String((responseData as any).message)
      normalized.code = responseStatus
    } else if ((responseData as any)?.message) {
      normalized.message = String((responseData as any).message)
    }

    if (responseStatus === 401) {
      normalized.code = 401
    }

    if (!config.skipErrorInterceptor) {
      normalized = await this.executeErrorInterceptors(normalized)
    }

    return normalized
  }

  /**
   * 线性重试：循环方式，避免递归与重复进入 retry 分支。
   */
  private async retryRequest<T>(url: string, config: RequestConfig, retryCount: number): Promise<HttpResponse<T>> {
    const attempts = Math.max(0, retryCount)
    const baseConfig: RequestConfig = { ...config, retry: false, retryCount: 0 }

    for (let i = 0; i <= attempts; i++) {
      try {
        return await this.request<T>(url, baseConfig)
      } catch (error) {
        const isLast = i === attempts
        if (isLast) {
          throw error
        }
        await new Promise(resolve => setTimeout(resolve, config.retryDelay || 1000))
      }
    }

    throw this.createRequestError('Request failed after retries', 'RETRY_EXCEEDED', undefined, config)
  }

  /**
   * 生成请求/缓存 key，包含 method + url + query + body。
   */
  private getCacheKey(url: string, config: RequestConfig) {
    const method = config.method || 'GET'
    const params = config.params ? `?${this.buildQueryString(config.params)}` : ''
    let bodyKey = ''

    if (config.data && !['GET', 'HEAD'].includes((method || '').toUpperCase())) {
      try {
        bodyKey = `:body=${JSON.stringify(config.data)}`
      } catch {
        bodyKey = `:body=${String(config.data)}`
      }
    }

    return `${method}:${url}${params}${bodyKey}`
  }

  /**
   * 构造去重用的请求 key，包含 method + url + query + body。
   */
  private getRequestKey(url: string, config: RequestConfig) {
    return this.getCacheKey(url, config)
  }

  /**
   * 核心请求方法：处理缓存、拦截器、超时、重试等逻辑。
   * 一般业务侧使用 get/post 等语法糖即可，除非需要更底层的控制。
   */
  async request<T = unknown>(url: string, config: RequestConfig = {}): Promise<HttpResponse<T>> {
    const useCustomCache = config.cache === true || (config as any).useCustomCache === true

    if (useCustomCache) {
      const cacheKey = this.getCacheKey(url, config)
      const cached = this.cache.get(cacheKey)

      if (cached && cached.expireTime > Date.now()) {
        return cached.data as HttpResponse<T>
      }
    }

    const mergedConfig: RequestConfig = {
      timeout: this.defaultTimeout,
      retry: false,
      retryCount: 3,
      retryDelay: 1000,
      cancelDuplicate: false,
      cache: false,
      cacheTime: DEFAULT_CACHE_TIME,
      ...config
    }

    const requestKey = mergedConfig.requestId || this.getRequestKey(url, mergedConfig)
    let abortController: AbortController | null = null

    if (!mergedConfig.signal) {
      abortController = new AbortController()
      mergedConfig.signal = abortController.signal
    }

    if (mergedConfig.cancelDuplicate) {
      const existing = this.pendingRequests.get(requestKey)

      if (existing) {
        existing.abortController.abort()
      }

      if (!abortController) {
        abortController = new AbortController()
        mergedConfig.signal = abortController.signal
      }

      this.pendingRequests.set(requestKey, { requestId: requestKey, abortController })
    }

    const finalConfig = mergedConfig.skipRequestInterceptor ? mergedConfig : await this.executeRequestInterceptors(mergedConfig)
    const fullURL = this.buildURL(url)
    let requestURL = fullURL

    if (finalConfig.params && Object.keys(finalConfig.params).length > 0) {
      const query = this.buildQueryString(finalConfig.params)
      requestURL = `${fullURL}${fullURL.includes('?') ? '&' : '?'}${query}`
    }

    const fetchOptions: RequestInit = {
      method: finalConfig.method || 'GET',
      headers: finalConfig.headers,
      signal: finalConfig.signal
    }

    if (finalConfig.mode) fetchOptions.mode = finalConfig.mode
    if (finalConfig.credentials) fetchOptions.credentials = finalConfig.credentials
    if (typeof (finalConfig as any).fetchCache === 'string') fetchOptions.cache = (finalConfig as any).fetchCache
    else if (typeof finalConfig.cache === 'string') fetchOptions.cache = finalConfig.cache
    if (finalConfig.redirect) fetchOptions.redirect = finalConfig.redirect
    if (finalConfig.referrer) fetchOptions.referrer = finalConfig.referrer
    if (finalConfig.referrerPolicy) fetchOptions.referrerPolicy = finalConfig.referrerPolicy
    if (finalConfig.integrity) fetchOptions.integrity = finalConfig.integrity
    if (typeof finalConfig.keepalive === 'boolean') fetchOptions.keepalive = finalConfig.keepalive
    // `window` 在 TS 的 DOM 定义中类型较窄，这里仅透传用户传入配置，使用 any 避免类型冲突
    if (finalConfig.window !== undefined) (fetchOptions as any).window = finalConfig.window

    const method = (fetchOptions.method || 'GET').toUpperCase()
    if (finalConfig.data && !['GET', 'HEAD'].includes(method)) {
      if (finalConfig.headers?.['Content-Type']?.includes('application/json')) {
        fetchOptions.body = JSON.stringify(finalConfig.data)
      } else if (finalConfig.data instanceof FormData) {
        fetchOptions.body = finalConfig.data as any
        delete (fetchOptions.headers as Record<string, string>)?.['Content-Type']
      } else {
        // data 类型为 unknown，向下传递给 fetch 时进行类型断言，保证灵活性
        fetchOptions.body = finalConfig.data as any
      }
    }

    if (finalConfig.loading) {
      this.loadingCount++
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        if (abortController) {
          abortController.abort()
        }
        reject(this.createRequestError('Request timeout', 'TIMEOUT', undefined, finalConfig))
      }, finalConfig.timeout)
    })

    const loadingStore = useLoadingStore()
    const showGlobalLoading = config.loading !== false // 默认为 true

    try {
       if (showGlobalLoading) {
        loadingStore.startLoading()
      }

      const fetchRaw = (globalThis as any)?.$fetch?.raw // 优先使用 Nuxt 的 $fetch.raw，保留 _data
      let response: Response | FetchResponse<T>

      const requestPromise = fetchRaw
        ? fetchRaw(requestURL, fetchOptions as any)
        : fetch(requestURL, fetchOptions)

      response = await Promise.race([requestPromise, timeoutPromise])

      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      const httpResponse = await this.handleResponse<T>(response, finalConfig)

      if (useCustomCache && ['GET', 'HEAD'].includes(method)) {
        const cacheKey = this.getCacheKey(url, finalConfig)
        this.cache.set(cacheKey, {
          data: httpResponse,
          expireTime: Date.now() + (finalConfig.cacheTime || DEFAULT_CACHE_TIME)
        })
      }

      return httpResponse
    } catch (error: any) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      // 当使用 $fetch.raw 时，非 2xx 会抛错，但在业务上视为请求成功（success=false）
      const responseFromError = (error as { response?: Response | FetchResponse<T> })?.response
      if (responseFromError) {
        return this.handleResponse<T>(responseFromError, finalConfig)
      }

      // 检查是否是中止错误（包括 AbortError 和 signal abort 相关错误）
      const isAbortError = error?.name === 'AbortError'
        || error?.name === 'AbortSignal'
        || (error?.message && (
          error.message.toLowerCase().includes('abort')
          || error.message.toLowerCase().includes('signal is aborted')
        ))

      if (isAbortError) {
        // 静默处理中止错误，不记录日志，不显示错误提示
        throw error
      }

      if (finalConfig.retry && (finalConfig.retryCount ?? 0) > 0) {
        return this.retryRequest(url, finalConfig, finalConfig.retryCount || 1)
      }

      const normalized = await this.handleError(error, finalConfig)
      throw normalized
    } finally {
      if (showGlobalLoading) {
        loadingStore.stopLoading()
      }
      
      if (finalConfig.loading) {
        this.loadingCount = Math.max(0, this.loadingCount - 1)
      }

      if (finalConfig.cancelDuplicate) {
        this.pendingRequests.delete(requestKey)
      }
    }
  }

  /**
   * 语法糖：GET 请求。
   */
  async get<T = unknown>(url: string, config?: RequestConfig) {
    return this.request<T>(url, { ...config, method: 'GET' })
  }

  /**
   * 语法糖：POST 请求。
   */
  async post<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { ...config, method: 'POST', data })
  }

  /**
   * 语法糖：PUT 请求。
   */
  async put<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { ...config, method: 'PUT', data })
  }

  /**
   * 语法糖：PATCH 请求。
   */
  async patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { ...config, method: 'PATCH', data })
  }

  /**
   * 语法糖：DELETE 请求。
   */
  async delete<T = unknown>(url: string, config?: RequestConfig) {
    return this.request<T>(url, { ...config, method: 'DELETE' })
  }

  /**
   * 根据 requestId 取消指定请求。
   */
  cancelRequest(requestId: string) {
    const pending = this.pendingRequests.get(requestId)

    if (pending) {
      pending.abortController.abort()
      this.pendingRequests.delete(requestId)
    }
  }

  /**
   * 取消所有正在进行的请求（常用于路由切换、登出）。
   */
  cancelAllRequests() {
    this.pendingRequests.forEach(pending => pending.abortController.abort())
    this.pendingRequests.clear()
  }

  /**
   * 清空手动缓存。
   */
  clearCache() {
    this.cache.clear()
  }

  /**
   * 当前是否存在 loading 中的请求（>0 即为 true）。
   */
  isLoading() {
    return this.loadingCount > 0
  }
}

const http = new HttpRequest()

export default http
